import { createApp } from "vue";
import "@fontsource/open-sans/400.css";
import "@fontsource/open-sans/600.css";
import "@fontsource/open-sans/700.css";
import App from "./App.vue";
import router from "./router";
import "./stylesheets/main.scss";
import { createStore } from "vuex";
import createPersistedState from "vuex-persistedstate";
import subDays from "date-fns/subDays";
import { installApiCompat } from "./apiCompat";
const { STATE_VERSION, resetOutdatedPersistedState } = require("./persistedState.cjs");
const { normalizeParticipantStatuses, statusIdFor, statusCodeFor } = require("./participantStatuses.cjs");

require("./interceptor.js");
// Old installations may still have a registration; startup never registers or reloads.
navigator.serviceWorker?.getRegistrations()
  .then(registrations => Promise.all(registrations.map(registration => registration.unregister())))
  .catch(error => console.warn("Unable to remove an obsolete service worker.", error));
resetOutdatedPersistedState(window.localStorage);

let participantStatusesRequest = null;

function clearSessionState(state) {
  state.events = [];
  state.event = null;
  state.program = null;
  state.eventProgramId = null;
  state.scanInfo = null;
  state.name = "";
  state.lastName = "";
  state.eventName = "";
  state.programName = "";
  state.participantStatuses = [];
  state.checkInSettings = null;
  state.checkInSettingsError = "";
  state.authToken = { token: null, date: null };
}

const store = createStore({
  plugins: [
    createPersistedState({
      paths: [
        "stateVersion",
        "authToken",
        "events",
        "name",
        "lastName",
        "event",
        "eventProgramId",
        "program",
        "eventName",
        "programName", 
        "scanInfo"
      ],
    }),
  ],
  state() {
    return {
      stateVersion: STATE_VERSION,
      authToken: { token: null, date: null },
      isScanned: Boolean,
      events: Array,
      program: Object,
      eventProgramId: null,
      programs: Array,
      event: Object,
      attendeeFilter: 0,
      eventName: String,
      programName: String,
      scanInfo: Object,
      checkInSettings: null,
      checkInSettingsError: "",
      participantStatuses: [],
      name: String,
      lastName: String,
    };
  },
  mutations: {
    setAuthToken(state, payLoad) {
      state.authToken = payLoad.token;
      state.name = payLoad.name;
      state.lastName = payLoad.lastName;
    },
    setScanned(state, payLoad) {
      state.isScanned = payLoad.isScanned;
    },
    setScanInfo(state, payLoad) {
      state.scanInfo = payLoad.scanInfo;
    },
    setCheckInSettings(state, settings) {
      state.checkInSettings = settings;
    },
    setCheckInSettingsError(state, message) {
      state.checkInSettingsError = message;
    },
    setParticipantStatuses(state, statuses) {
      state.participantStatuses = statuses;
    },
    setEvents(state, payLoad) {
      state.events = payLoad.events;
    },
    setProgram(state, payLoad) {
      state.program = payLoad.program;
    },
    setEventProgramId(state, payLoad) {
      state.eventProgramId = payLoad.eventProgramId;
    },
    setPrograms(state, payLoad) {
      state.programs = payLoad.programs;
    },
    setEvent(state, payLoad) {
      state.event = payLoad.event;
    },
    setAttendeeFilter(state, payLoad) {
      state.attendeeFilter = payLoad.filter;
    },
    setEventName(state, payLoad) {
      state.eventName = payLoad.eventName;
    },
    setProgramName(state, payLoad) {
      state.programName = payLoad.programName;
    },
    clearSession(state) {
      clearSessionState(state);
    },
    logout(state) {
      clearSessionState(state);
      router.push("/");
    },
  },
  actions: {
    async loadParticipantStatuses({ state, commit, getters }) {
      if (state.participantStatuses.length) return state.participantStatuses;
      if (!getters.getAuthToken?.token) throw new Error("A session is required to load participant statuses.");
      if (!participantStatusesRequest) {
        participantStatusesRequest = (async () => {
          const response = await fetch(process.env.VUE_APP_ROOT_API.replace(/\/rest$/, "") + "/v1/participant-statuses", {
            headers: { Authorization: `Bearer ${getters.getAuthToken.token}` },
          });
          if (!response.ok) throw new Error("Unable to load participant statuses.");
          const statuses = normalizeParticipantStatuses(await response.json());
          commit("setParticipantStatuses", statuses);
          return statuses;
        })().finally(() => { participantStatusesRequest = null; });
      }
      return participantStatusesRequest;
    },
    async loadCheckInSettings({ state, commit, getters }, eventId) {
      if (!eventId) return null;
      if (state.checkInSettings?.eventId === eventId) return state.checkInSettings;
      commit("setCheckInSettings", null);
      commit("setCheckInSettingsError", "");
      try {
        const response = await fetch(process.env.VUE_APP_ROOT_API + "/v1/DataAPI/GetCheckinSettings/json?" + new URLSearchParams({ EventID: eventId, AuthToken: getters.getAuthToken.token }));
        if (!response.ok) throw new Error("Unable to load check-in settings.");
        const result = await response.json();
        if (result.serviceStatus !== "OK") throw new Error("Unable to load check-in settings.");
        commit("setCheckInSettings", result.data);
        return result.data;
      } catch (error) {
        commit("setCheckInSettingsError", "Event settings are unavailable. Photo capture may not be available.");
        throw error;
      }
    },
  },
  getters: {
    getAuthToken(state) {
      return state.authToken;
    },
    getScanned(state) {
      return state.isScanned;
    },
    getEvents(state) {
      return state.events;
    },
    isAuthenticated(state) {
      return (
        state.authToken.token != null &&
        state.authToken.date != null &&
        new Date(state.authToken.date) > subDays(new Date(), 1)
      );
    },
    getProgram(state) {
      return state.program;
    },
    getEventProgramId(state) {
      return state.eventProgramId;
    },
    getPrograms(state) {
      return state.programs;
    },
    getEvent(state) {
      return state.event;
    },
    getAttendeeFilter(state) {
      return state.attendeeFilter;
    },
    getEventName(state) {
      return state.eventName;
    },
    getProgramName(state) {
      return state.programName;
    },
    getScanInfo(state) {
      return state.scanInfo;
    },
    getCheckInSettings(state) {
      return state.checkInSettings;
    },
    getCheckInSettingsError(state) {
      return state.checkInSettingsError;
    },
    getParticipantStatusId(state) {
      return code => statusIdFor(state.participantStatuses, code);
    },
    getParticipantStatusCode(state) {
      return statusId => statusCodeFor(state.participantStatuses, statusId);
    },
    getLoggedInUser(state) {
      return state.name + " " + state.lastName;
    },
  },
});

installApiCompat(store);

export default store;

createApp(App).use(router).use(store).mount("#app");
